import React, { Dispatch } from 'react';
import { Grid, GridRow, GridColumn } from 'semantic-ui-react';
import Button from '@material-ui/core/Button';
import { AgGridReact } from 'ag-grid-react';
import i18next from 'i18next';
import { MenuDto } from 'src/services/Client';
import HATEOASClient from 'src/services/HATEOASClient';
import { connect } from 'react-redux';
import { AnyAction, compose } from 'redux';
import GeneralActions from 'src/redux/actions/GeneralActions';
import { IReduxState } from 'src/redux/IReduxState';
import { ColDef, RowStyle, GridReadyEvent, GridApi } from 'ag-grid-community';
import Select, { Option } from 'react-select';
import Search from '@material-ui/icons/Search';
import RotateLeft from '@material-ui/icons/RotateLeft';
import EditOutlined from '@material-ui/icons/EditOutlined';
import Tooltip from '@material-ui/core/Tooltip';
import Dialog from 'src/components/Dialogs/Dialog';
import { Button as ActionButton } from 'src/components/Button';
import { orderBy } from 'lodash';
import { renderErrorToast, renderSuccessToast } from 'src/utils/FeedbackMessageUtils';

type SortDirection = 'asc' | 'desc' | null;

interface ITextTabProps {
    client: HATEOASClient;
    baseLinks?: MenuDto;
    addLoadingMessage: (message: string) => void;
    removeLoadingMessage: (message: string) => void;
    t: i18next.TranslationFunction;
    ReceiveTranslationServiceAction: (translationService: any) => void;
    userDetails?: any;
}

interface ITextTabState {
    rowData: any[];
    searchQuery: string;
    language: string;
    category: string;
    textElements: any[];
    expandedPaths: string[];
    usagesData: { [textId: number]: any[] };
    sortField: string | null;
    sortDirection: SortDirection;
    editingText?: any;
    editContent: string;
    isEditDialogOpen: boolean;
    isConfirmDialogOpen: boolean;
    isSavingEdit: boolean;
}

const SortableHeader = (props: any) => {
    const { displayName, field, getSort, onSort } = props;
    const { sortField, sortDirection } = getSort();
    const isActive = sortField === field;
    let arrow = '\u2195';
    if (isActive && sortDirection === 'asc') arrow = '\u2191';
    else if (isActive && sortDirection === 'desc') arrow = '\u2193';

    return (
        <div
            onClick={() => onSort(field)}
            style={{
                cursor: 'pointer',
                userSelect: 'none',
                width: '100%',
                textAlign: 'center',
                color: isActive ? '#ffc107' : 'inherit',
                fontWeight: isActive ? 700 : 'inherit',
            }}
        >
            {displayName}
            <span style={{ fontSize: '12px', marginLeft: '2px' }}>{arrow}</span>
        </div>
    );
};

class TextTab extends React.Component<ITextTabProps, ITextTabState> {
    constructor(props: ITextTabProps) {
        super(props);
        this.state = {
            rowData: [],
            searchQuery: '',
            language: '',
            category: 'COMMENT',
            textElements: [],
            expandedPaths: [],
            usagesData: {},
            sortField: 'usageCount',
            sortDirection: 'desc',
            editContent: '',
            isEditDialogOpen: false,
            isConfirmDialogOpen: false,
            isSavingEdit: false,
        };
    }

    gridApi?: GridApi;

    onGridReady = (event: GridReadyEvent) => {
        this.gridApi = event.api;
    }

    componentDidMount(): void {
        this.checkPrivilegesAndLoad();
    }

    componentDidUpdate(prevProps: ITextTabProps): void {
        if (!prevProps.userDetails && this.props.userDetails) {
            this.checkPrivilegesAndLoad();
        }
    }

    get hasPrivilege() {
        const { userDetails } = this.props;
        return (
            userDetails &&
            userDetails.privileges &&
            userDetails.privileges.some(
                (priv: any) =>
                    priv.identifier === 'ADMIN_AREA_READ' ||
                    priv.identifier === 'ADMIN_AREA_WRITE',
            )
        );
    }

    checkPrivilegesAndLoad = () => {
        if (this.hasPrivilege) {
            this.loadTexts();
        }
    }

    get languageOptions(): Option[] {
        const { t } = this.props;
        return [
            { value: '', label: t('GLOBAL:ALL') },
            { value: 'de', label: t('GLOBAL:LANG_DE') },
            { value: 'en', label: t('GLOBAL:LANG_EN') },
        ];
    }

    get categoryOptions(): Option[] {
        return [{ value: 'COMMENT', label: 'Comment' }];
    }

    getSortState = () => ({
        sortField: this.state.sortField,
        sortDirection: this.state.sortDirection,
    })

    handleSort = (field: string) => {
        this.setState(
            (prev: ITextTabState) => {
                let direction: SortDirection = 'asc';
                if (prev.sortField === field) {
                    if (prev.sortDirection === 'asc') direction = 'desc';
                    else if (prev.sortDirection === 'desc') direction = null;
                }
                return {
                    sortField: direction ? field : null,
                    sortDirection: direction,
                } as ITextTabState;
            },
            () => {
                this.rebuildRows();
                if (this.gridApi) this.gridApi.refreshHeader();
            },
        );
    }

    sortTextElements = (elements: any[]): any[] => {
        const { sortField, sortDirection } = this.state;
        if (!sortField || !sortDirection) return elements;
        return orderBy(elements, [(el: any) => el[sortField] || 0], [sortDirection]);
    }

    sortUsages = (usages: any[]): any[] => {
        // Sort model years numerically, ascending by default
        return orderBy(usages, [(usage: any) => Number(usage.modelYear) || 0], ['asc']);
    }

    sortTypeFamilies = (typeFamilies: any[]): any[] => {
        // Sort type families by name, ascending
        return orderBy(typeFamilies, [(tf: any) => tf.typeFamilyName || ''], ['asc']);
    }

    sortDerivatives = (derivatives: any[]): any[] => {
        // Sort derivatives by name, ascending
        return orderBy(derivatives, [(der: any) => der.derivativeName || ''], ['asc']);
    }

    toggleRow = (row: any) => {
        const { expandedPaths, usagesData } = this.state;
        const isExpanded = expandedPaths.includes(row.nodeId);

        if (isExpanded) {
            const newPaths = expandedPaths.filter((id) => id !== row.nodeId);
            this.setState({ expandedPaths: newPaths }, () => this.rebuildRows());
            return;
        }

        const nextPaths = [...expandedPaths, row.nodeId];

        // Text level: fetch usages drill-down once, then render
        if (row.level === 0 && !usagesData[row.objectId]) {
            this.setState({ expandedPaths: nextPaths }, () =>
                this.loadTextUsages(row.objectId),
            );
        } else {
            this.setState({ expandedPaths: nextPaths }, () => this.rebuildRows());
        }
    }

    loadTexts = () => {
        const { client, baseLinks, addLoadingMessage, removeLoadingMessage } = this.props;
        const { searchQuery, language, category } = this.state;

        if (!baseLinks) return;

        addLoadingMessage('Loading texts');
        const params: any = { type: category, size: 0 };
        if (language) params.lang = language;
        if (searchQuery) params.query = encodeURIComponent(searchQuery);

        client
            .GET_texts(baseLinks)
            .execute(params)
            .then((response: any) => {
                removeLoadingMessage('Loading texts');
                this.setState(
                    {
                        textElements: (response && response.elements) || [],
                        expandedPaths: [],
                        usagesData: {},
                    },
                    () => this.rebuildRows(),
                );
            })
            .catch((error: any) => {
                removeLoadingMessage('Loading texts');
                console.error('Failed to load texts', error);
            });
    }

    loadTextUsages = (textId: number) => {
        const { client, baseLinks, addLoadingMessage, removeLoadingMessage } = this.props;
        if (!baseLinks) return;

        addLoadingMessage('Loading texts');
        client
            .GET_textUsages(baseLinks, textId)
            .execute()
            .then((response: any) => {
                removeLoadingMessage('Loading texts');
                const modelYears = Array.isArray(response)
                    ? response
                    : Array.isArray(response && response.modelYears)
                        ? response.modelYears
                        : Array.isArray(response && response.elements)
                            ? response.elements
                            : [];
                this.setState(
                    (prev: ITextTabState) => ({
                        usagesData: {
                            ...prev.usagesData,
                            [textId]: modelYears,
                        },
                    }),
                    () => this.rebuildRows(),
                );
            })
            .catch((error: any) => {
                removeLoadingMessage('Loading texts');
                console.error('Failed to load text usages', error);
            });
    }

    clearFilters = () => {
        this.setState(
            {
                searchQuery: '',
                language: '',
                category: 'COMMENT',
            },
            () => this.loadTexts(),
        );
    }

    openEditDialog = (row: any) => {
        this.setState({
            editingText: row,
            editContent: row && row.content ? String(row.content) : '',
            isEditDialogOpen: true,
            isConfirmDialogOpen: false,
        });
    }

    closeEditFlow = () => {
        this.setState({
            editingText: undefined,
            editContent: '',
            isEditDialogOpen: false,
            isConfirmDialogOpen: false,
            isSavingEdit: false,
        });
    }

    openConfirmDialog = () => {
        if (!this.isEditChanged()) return;
        this.setState({
            isEditDialogOpen: false,
            isConfirmDialogOpen: true,
        });
    }

    backToEditDialog = () => {
        this.setState({
            isEditDialogOpen: true,
            isConfirmDialogOpen: false,
        });
    }

    isEditChanged = () => {
        const { editingText, editContent } = this.state;
        if (!editingText) return false;
        const currentContent = editingText.content ? String(editingText.content).trim() : '';
        const nextContent = editContent.trim();
        return nextContent.length > 0 && nextContent !== currentContent;
    }

    saveEditedText = () => {
        const { client, baseLinks, addLoadingMessage, removeLoadingMessage, t } = this.props;
        const { editingText, editContent } = this.state;
        if (!baseLinks || !editingText) return;

        const nextContent = editContent.trim();
        if (!nextContent) return;

        const loadingMessage = 'Saving text';
        this.setState({ isSavingEdit: true });
        addLoadingMessage(loadingMessage);

        client
            .PATCH_texts(baseLinks, editingText.objectId)
            .execute({
                content: nextContent,
            })
            .then(() => {
                removeLoadingMessage(loadingMessage);
                renderSuccessToast(
                  {
                    message: 'MANAGEMENT_VIEW:TEXT_SUCCESS',
                    details: `Text ID ${editingText.objectId} - ${t('MANAGEMENT_VIEW:TEXT_SUCCESS')}`,
                    translatedDetails: true,
                    timestamp: Date.now(),
                  },
                  7000,
                );
                this.closeEditFlow();
                this.loadTexts();
            })
            .catch((error: any) => {
                removeLoadingMessage(loadingMessage);
                this.setState({ isSavingEdit: false });
                renderErrorToast({
                    message: 'MANAGEMENT_VIEW:TEXT_FAILURE',
                    details: error,
                    timestamp: Date.now(),
                });
                console.error('Failed to update text', error);
            });
    }

    renderEditDialog = () => {
        const { t } = this.props;
        const { editingText, editContent, isEditDialogOpen } = this.state;
        if (!isEditDialogOpen || !editingText) return null;

        return (
            <Dialog
                open={true}
                title={t('MANAGEMENT_VIEW:TEXT_EDIT_CONTENT')}
                customStyleClassName={'BaseModal text-edit-dialog'}
                onRequestClose={this.closeEditFlow}
                actions={[
                    <ActionButton
                        key={'continue'}
                        icon={'checkmark'}
                        disabled={!this.isEditChanged()}
                        onClick={this.openConfirmDialog}
                    />,
                    <ActionButton
                        key={'cancel'}
                        icon={'ban'}
                        onClick={this.closeEditFlow}
                    />,
                ]}
            >
                <div style={{ marginBottom: '12px' }}>
                    <b>{t('TEXT_ID')}:</b> {editingText.objectId}
                </div>
                <div style={{ marginBottom: '8px', fontWeight: 600 }}>{t('MANAGEMENT_VIEW:TEXT_CONTENT')}</div>
                <textarea
                    className="text-edit-content-input"
                    value={editContent}
                    onChange={(event) => this.setState({ editContent: event.target.value })}
                />
            </Dialog>
        );
    }

    renderConfirmDialog = () => {
        const { t } = this.props;
        const { editingText, editContent, isConfirmDialogOpen, isSavingEdit } = this.state;
        if (!isConfirmDialogOpen || !editingText) return null;

        return (
            <Dialog
                open={true}
                title={t('MANAGEMENT_VIEW:TEXT_CONFIRM_UPDATE')}
                customStyleClassName={'BaseModal text-edit-dialog'}
                onRequestClose={this.closeEditFlow}
                actions={[
                    <ActionButton
                        key={'back'}
                        icon={'arrow left'}
                        className={'text-dialog-back-btn'}
                        disabled={isSavingEdit}
                        onClick={this.backToEditDialog}
                    />,
                    <ActionButton
                        key={'confirm'}
                        icon={'checkmark'}
                        disabled={isSavingEdit}
                        onClick={this.saveEditedText}
                    />,
                    <ActionButton
                        key={'cancel'}
                        icon={'ban'}
                        disabled={isSavingEdit}
                        onClick={this.closeEditFlow}
                    />,
                ]}
            >
                <p>
                    {t('MANAGEMENT_VIEW:TEXT_CHANGE')}{' '}
                    <b>{editingText.usageCount || 0}</b> {t('MANAGEMENT_VIEW:TEXT_USAGE')}{' '}
                    <b>{editingText.modelYearCount || 0}</b> {t('BUSINESS_APPROVAL_MODEL_YEAR')},{' '}
                    <b>{editingText.typeFamilyCount || 0}</b> {t('BUSINESS_APPROVAL_FAMILY')},{' '}
                    <b>{editingText.derivativeCount || 0}</b> {t('TEXT_DERIVATES')}.
                </p>
                <div style={{ marginTop: '16px' }}>
                    <div style={{ marginBottom: '6px', fontWeight: 600 }}>
                        {t('MANAGEMENT_VIEW:TEXT_NEW_CONTENT')}
                    </div>
                    <div
                        className="text-confirm-content-box"
                    >
                        {editContent.trim()}
                    </div>
                </div>
            </Dialog>
        );
    }

    rebuildRows = () => {
        this.setState({ rowData: this.buildDisplayRows() });
    }

    buildDisplayRows = (): any[] => {
        const { textElements, expandedPaths, usagesData } = this.state;
        const rows: any[] = [];

        this.sortTextElements(textElements).forEach((text: any) => {
            const textNodeId = `text-${text.objectId}`;
            rows.push({
                ...text,
                nodeId: textNodeId,
                level: 0,
                expandable: (text.usageCount || 0) > 0,
                displayContent: text.content,
                occurrences: text.usageCount,
                iNumber: '',
            });

            if (!expandedPaths.includes(textNodeId)) return;

            const usages = usagesData[text.objectId] || [];
            this.sortUsages(usages).forEach((year: any) => {
                const yearNodeId = `my-${text.objectId}-${year.modelYearId}`;
                rows.push({
                    nodeId: yearNodeId,
                    level: 1,
                    expandable: (year.typeFamilies || []).length > 0,
                    displayContent: year.modelYear,
                    occurrences: '',
                    iNumber: '',
                });

                if (!expandedPaths.includes(yearNodeId)) return;

                this.sortTypeFamilies(year.typeFamilies || []).forEach((tf: any) => {
                    const tfNodeId = `tf-${text.objectId}-${year.modelYearId}-${tf.typeFamilyId}`;
                    rows.push({
                        nodeId: tfNodeId,
                        level: 2,
                        expandable: (tf.derivatives || []).length > 0,
                        displayContent: tf.typeFamilyName,
                        occurrences: '',
                        iNumber: '',
                    });

                    if (!expandedPaths.includes(tfNodeId)) return;

                    this.sortDerivatives(tf.derivatives || []).forEach((der: any, index: number) => {
                        rows.push({
                            nodeId: `der-${text.objectId}-${year.modelYearId}-${tf.typeFamilyId}-${der.derivativeId || index}`,
                            level: 3,
                            expandable: false,
                            displayContent: der.derivativeName && der.btyp
                                ? `${der.derivativeName} (${der.btyp})`
                                : (der.derivativeName || '—'),
                            occurrences: '',
                            iNumber: Array.isArray(der.inumbers) ? der.inumbers.join(', ') : '',
                        });
                    });
                });
            });
        });

        return rows;
    }

    get columnDefinitions(): ColDef[] {
        const { t } = this.props;
        const { expandedPaths } = this.state;
        const colorByLevel: { [key: number]: string } = {
            0: '#1565c0',
            1: '#2e7d32',
            2: '#e65100',
            3: '#6a1b9a',
        };

        return [
            {
                field: 'occurrences',
                headerName: t('TEXT_TOTAL'),
                width: 150,
                sortable: false,
                cellStyle: { fontWeight: 600 },
                headerComponent: SortableHeader,
                headerComponentParams: {
                    field: 'usageCount',
                    getSort: this.getSortState,
                    onSort: this.handleSort,
                },
                valueGetter: (params: any) =>
                    params.data && params.data.level === 0 ? params.data.occurrences : '',
            },
            {
                field: 'category',
                headerName: t('TEXT_CATEGORY'),
                width: 110,
                valueGetter: (params: any) =>
                    params.data && params.data.level === 0 ? params.data.category : '',
            },
            {
                field: 'objectId',
                headerName: t('TEXT_ID'),
                width: 90,
                valueGetter: (params: any) =>
                    params.data && params.data.level === 0 ? params.data.objectId : '',
            },
            {
                headerName: t('TEXT_CONTENT'),
                field: 'displayContent',
                flex: 1,
                minWidth: 280,
                cellRenderer: (params: any) => {
                    if (!params.data) return null;
                    const { level, expandable, nodeId, displayContent } = params.data;
                    const isOpen = expandedPaths.includes(nodeId);
                    const text = (displayContent === null || displayContent === undefined)
                        ? ''
                        : String(displayContent);
                    const singleLine = text.replace(/\s+/g, ' ').trim();

                    return (
                        <div
                            title={text}
                            className="text-content-cell"
                            style={{
                                cursor: expandable ? 'pointer' : 'default',
                                paddingLeft: level * 22,
                                color: colorByLevel[level],
                                fontWeight: level === 0 ? 500 : 400,
                            }}
                            onClick={() => expandable && this.toggleRow(params.data)}
                        >
                            {expandable ? (
                                <span style={{ marginRight: 8, fontSize: '9px' }}>
                                    {isOpen ? '▼' : '▶'}
                                </span>
                            ) : (
                                <span style={{ display: 'inline-block', width: 14, marginRight: 8 }} />
                            )}
                            <span
                                className="text-content-cell-text"
                                style={{
                                    paddingRight: level === 0 ? '8px' : '0',
                                }}
                            >
                                {singleLine}
                            </span>
                            {level === 0 ? (
                                <Tooltip title={t('MANAGEMENT_VIEW:TEXT_EDIT_CONTENT')} placement="top">
                                    <span
                                        className="text-edit-icon-action"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            this.openEditDialog(params.data);
                                        }}
                                    >
                                        <EditOutlined style={{ fontSize: 16 }} />
                                    </span>
                                </Tooltip>
                            ) : null}
                        </div>
                    );
                },
            },
            {
                field: 'iNumber',
                headerName: t("TEXT_I_NUMBER"),
                width: 150,
                cellRenderer: (params: any) => {
                    const value =
                        params.data && params.data.level === 3 && params.data.iNumber
                            ? String(params.data.iNumber)
                            : '';

                    return (
                        <div
                            title={value}
                            style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                width: '100%',
                            }}
                        >
                            {value}
                        </div>
                    );
                },
            },
            {
                field: 'modelYearCount',
                headerName: t('BUSINESS_APPROVAL_MODEL_YEAR'),
                width: 115,
                valueGetter: (params: any) =>
                    params.data && params.data.level === 0 ? params.data.modelYearCount : '',
            },
            {
                field: 'typeFamilyCount',
                headerName:  t("BUSINESS_APPROVAL_FAMILY"),
                width: 115,
                valueGetter: (params: any) =>
                    params.data && params.data.level === 0 ? params.data.typeFamilyCount : '',
            },
            {
                field: 'derivativeCount',
                headerName: t('TEXT_DERIVATES'),
                width: 95,
                valueGetter: (params: any) =>
                    params.data && params.data.level === 0 ? params.data.derivativeCount : '',
            },
            {
                field: 'langCode',
                headerName: t('TEXT_LANGUAGE'),
                width: 95,
                valueGetter: (params: any) =>
                    params.data && params.data.level === 0
                        ? '-'
                        : '',
            },
        ];
    }

    getRowStyle = (params: any): RowStyle | undefined => {
        if (!params.data) return undefined;

        const bgByLevel: { [key: number]: string } = {
            0: '#ffffff',
            1: '#e3f2fd',
            2: '#f1f8e9',
            3: '#fff3e0',
        };

        return {
            background: bgByLevel[params.data.level] || '#ffffff',
            borderBottom: params.data.level === 0 ? '1px solid #ddd' : '1px solid #eee',
        };
    }

    render() {
        const { t } = this.props;
        if (!this.hasPrivilege) {
            return (
                <Grid className="managementview_container">
                    <GridRow centered={true} style={{ marginTop: '50px' }}>
                        <GridColumn width={8} textAlign="center">
                            <div
                                style={{
                                    padding: '40px',
                                    border: '1px dashed #ccc',
                                    borderRadius: '8px',
                                    backgroundColor: '#f9f9f9',
                                }}
                            >
                                <i
                                    className="lock icon"
                                    style={{ fontSize: '3rem', color: '#999' }}
                                />
                                <h3 style={{ color: '#666', margin: '5px' }}>
                                    {t('BUSINESS_APPROVAL_RESTRICTION')}
                                </h3>
                                <p style={{ marginTop: '10px', color: '#666' }}>
                                    {t('BUSINESS_APPROVAL_ACCESS_MESSAGE')}
                                </p>
                            </div>
                        </GridColumn>
                    </GridRow>
                </Grid>
            );
        }

        const selectedLanguage =
            this.languageOptions.find((opt) => opt.value === this.state.language);
        const selectedCategory =
            this.categoryOptions.find((opt) => opt.value === this.state.category);

        return (
            <Grid className="managementview_container">
                <GridColumn width={4} className="job_title">
                    {t('TEXT_TTTLE')}
                </GridColumn>
                <GridRow columns={16} className="grid_header" style={{ paddingRight: '0' }}>
                    <GridColumn width={16}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-end',
                                gap: '12px',
                                paddingRight: '0',
                                paddingLeft: '10px',
                            }}
                        >
                            <div
                                className="filter_dropdown"
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flex: '0 1 460px',
                                    minWidth: '260px',
                                    maxWidth: '460px',
                                }}
                            >
                                <label className="grid_label">Search</label>
                                <input
                                    type="text"
                                    className="date-picker-input"
                                    style={{
                                        height: '38px',
                                        padding: '0 10px',
                                        fontSize: '13px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        boxSizing: 'border-box',
                                    }}
                                    value={this.state.searchQuery}
                                    placeholder={t('TEXT_SEARCH_PLACEHOLDER')}
                                    onChange={(event) =>
                                        this.setState({ searchQuery: event.target.value })
                                    }
                                    onKeyPress={(event) => {
                                        if (event.key === 'Enter') this.loadTexts();
                                    }}
                                />
                            </div>

                            <div
                                className="filter_dropdown"
                                style={{ display: 'flex', flexDirection: 'column', minWidth: '160px' }}
                            >
                                <label className="grid_label">{t('TEXT_LANGUAGE')}</label>
                                <div style={{ width: '100%' }}>
                                    <Select
                                        clearable={false}
                                        searchable={false}
                                        value={selectedLanguage}
                                        options={this.languageOptions}
                                        onChange={(selected: Option | null) =>
                                            this.setState({
                                                language: selected ? (selected.value as string) : '',
                                            })
                                        }
                                        placeholder={t('TEXT_LANGUAGE')}
                                    />
                                </div>
                            </div>

                            <div
                                className="filter_dropdown"
                                style={{ display: 'flex', flexDirection: 'column', minWidth: '160px' }}
                            >
                                <label className="grid_label">{t('TEXT_CATEGORY')}</label>
                                <div style={{ width: '100%' }}>
                                    <Select
                                        clearable={false}
                                        searchable={false}
                                        value={selectedCategory}
                                        options={this.categoryOptions}
                                        onChange={(selected: Option | null) =>
                                            this.setState({
                                                category: selected
                                                    ? (selected.value as string)
                                                    : 'COMMENT',
                                            })
                                        }
                                        placeholder={t('TEXT_CATEGORY')}
                                    />
                                </div>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    gap: '8px',
                                    alignItems: 'center',
                                    marginBottom: '0',
                                    flexShrink: 0,
                                }}
                            >
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<Search />}
                                    onClick={this.loadTexts}
                                    style={{ whiteSpace: 'nowrap', height: '38px', padding: '0 14px' }}
                                >
                                    {t('BUSINESS_APPROVAL_SEARCH')}
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<RotateLeft />}
                                    onClick={this.clearFilters}
                                    style={{ whiteSpace: 'nowrap', height: '38px', padding: '0 14px' }}
                                >
                                    {t('RESET')}
                                </Button>
                            </div>
                        </div>
                    </GridColumn>
                </GridRow>

                <GridRow>
                    <GridColumn width={16}>
                        <div
                            className="ag-theme-alpine"
                            style={{ height: 500, width: '100%' }}
                        >
                            <AgGridReact
                                rowData={this.state.rowData}
                                columnDefs={this.columnDefinitions}
                                headerHeight={40}
                                rowHeight={45}
                                animateRows={true}
                                getRowStyle={this.getRowStyle}
                                getRowId={(params: any) => params.data.nodeId}
                                onGridReady={this.onGridReady}
                            />
                        </div>
                    </GridColumn>
                </GridRow>
                {this.renderEditDialog()}
                {this.renderConfirmDialog()}
            </Grid>
        );
    }
}

const mapStateToProps = (state: IReduxState) => ({
    userDetails: state.general.userDetails,
    baseLinks: state.general.baseLinks,
    client: state.general.client,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
    addLoadingMessage: (message: string) => dispatch(GeneralActions.AddLoadingMessage(message)),
    removeLoadingMessage: (message: string) => dispatch(GeneralActions.RemoveLoadingMessage(message)),
    ReceiveTranslationServiceAction: (payload: any) => dispatch(GeneralActions.ReceiveTranslationServiceAction(payload))
});

export default compose<React.ComponentType<any>>(
    connect(mapStateToProps, mapDispatchToProps)
)(TextTab);



// WEBPACK FOOTER //
// ./src/views/ManagementView/TextTab.tsx